import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Box, Button, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Tab, Tabs, Typography } from '@mui/material';
import SourceIcon from '@mui/icons-material/Source';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import CloseIcon from '@mui/icons-material/Close';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import HistoryIcon from '@mui/icons-material/History';
import { useTranslation } from 'react-i18next';
import { useElementSize } from '@custom-react-hooks/use-element-size';
import { useUserContext } from '@/contexts/userContext';
import TabPanel from '@/components/tabPanel';
import DataSourceInstanceAdministration from './administration';
import SettingsValidator from '../entities/settingsValidators';
import EntityConfiguration from '../entities/configuration';
import DataSourceItems from './dataSourceItems';
import RunHistory from './runHistory';
import ViewDataSourceItem from './viewDataSourceItem';
import PipelineBuilder from '../pipelineBuilder/pipelineBuilder';
import { useApiContext } from '@/contexts/apiContext';
import ProgressIndicator from '../progressIndicator';
import ErrorMessage from '../errorMessage';
import DataSourceHeader from './header';
import { useNotificationContext } from '@/contexts/notificationContext';

type Props = {};

const settingsValidator = new SettingsValidator();
const DataSourceInstance = ({}: Props) => {
  const { t } = useTranslation();
  const { hasPermission } = useUserContext();
  const apiContext = useApiContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useNotificationContext();

  const [containerRef, containerSize] = useElementSize();
  const [topPanelRef, topPanelSize] = useElementSize();

  const { dataSourceDefId, dataSourceInstanceId } = useParams();

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const [dataSourceInstance, setDataSourceInstance] = useState<any>();

  const [selectedTab, setSelectedTab] = useState<any>('dataSourceItems');
  const [openDataSourceItems, setOpenDataSourceItems] = useState<any[]>([]);
  const [optionTabs, setOptionTabs] = useState<any[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();

  const menuOpen = Boolean(menuAnchorEl);

  useEffect(() => {
    const getUserDataSource = async () => {
      try {
        setLoading(true);
        const response = await apiContext.getDataSourceInstance(
          dataSourceDefId as string,
          dataSourceInstanceId as string,
        );
        setDataSourceInstance(response);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    if (dataSourceDefId && dataSourceInstanceId) {
      getUserDataSource();
    }
  }, [dataSourceDefId, dataSourceInstanceId]);

  useEffect(() => {
    const [errors = {}, hasError = false] = settingsValidator.validateSettings(
      dataSourceInstance?.dataSourceDef.settingDefs,
      dataSourceInstance?.settings,
      t,
    );
    if (hasError) {
      setSelectedTab('configuration');
    }
  }, [dataSourceInstance?.dataSourceDef, dataSourceInstance?.settings]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  const onOpenDataSourceItem = (item: any) => {
    const f = openDataSourceItems.find((f) => f.id === item.id);
    if (f) {
      setSelectedTab(f.itemName);
    } else {
      setOpenDataSourceItems([...openDataSourceItems, item]);
      setSelectedTab(item.itemName);
    }
  };

  const addOptionsTab = (option: any) => {
    const o = optionTabs.find((o) => o.value === option.value);
    if (o) {
      setSelectedTab(o.value);
    } else {
      setOptionTabs([...optionTabs, option]);
      setSelectedTab(option.value);
    }
    closeMenu();
  };

  const deleteDataSourceItemTab = (itemName: string) => {
    setOpenDataSourceItems(openDataSourceItems.filter((f) => f.itemName !== itemName));
    setSelectedTab('dataSourceItems');
  };

  const deleteOptionTab = (value: string) => {
    setOptionTabs(optionTabs.filter((o) => o.value !== value));
    setSelectedTab('dataSourceItems');
  };

  const openOptionsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const updateDataSourceInstance = async (updatedDataSource: any) => {
    const { id, tenantId, name, description, entityType, entityDefId, settings, eventSubscriptions } =
      updatedDataSource;
    try {
      await apiContext.updateDataSourceInstance(entityDefId as string, id, {
        id,
        tenantId,
        name,
        description,
        entityType,
        entityDefId,
        settings,
        eventSubscriptions,
      });
      setDataSourceInstance(updatedDataSource);
      toast({ message: 'Changes saved' });
    } catch (error) {
      setError(error);
    }
  };

  const reloadDataSourceInstance = async () => {
    try {
      const response = await apiContext.getDataSourceInstance(
        dataSourceDefId as string,
        dataSourceInstanceId as string,
      );
      setDataSourceInstance(response);
    } catch (error) {
      setError(error);
    }
  };

  const close = () => {
    const from = location.state?.from || '/instances?tab=dataSourceInstances';
    navigate(from);
  };

  if (loading || !dataSourceInstance) {
    return <ProgressIndicator />;
  }

  const containerHeight = containerSize.height;
  const topPanelHeight = topPanelSize.height;

  return (
    <Stack
      sx={{
        height: '100%',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
      ref={containerRef}
    >
      <Stack ref={topPanelRef} sx={{ zIndex: 10 }}>
        <DataSourceHeader dataSourceInstance={dataSourceInstance} onClose={close} />
        {error && <ErrorMessage error={error} />}

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems="stretch"
          className="glass-card-light"
          sx={{
            mx: 1,
            my: 0.5,
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: 'none',
            overflow: 'hidden',
            pb: { xs: 1, sm: 0 },
          }}
        >
          <Box sx={{ maxWidth: { xs: '100%', sm: '75vw' }, width: '100%', pl: 1 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              indicatorColor="secondary"
              variant="scrollable"
              sx={{
                minHeight: '40px',
                '& .MuiTabs-indicator': {
                  height: '3px',
                  borderRadius: '3px 3px 0 0',
                  bgcolor: '#8BD8BC',
                },
              }}
            >
              {hasPermission(`pipeline:dataSources:items:view`, dataSourceInstance?.userAccess) && (
                <Tab
                  value={'dataSourceItems'}
                  icon={<SourceIcon fontSize="small" />}
                  iconPosition="start"
                  sx={{
                    minHeight: '40px',
                    px: 2,
                    transition: 'all 0.2s',
                    opacity: selectedTab === 'dataSourceItems' ? 1 : 0.7,
                    '&:hover': { opacity: 1, bgcolor: 'rgba(139, 216, 188, 0.05)' },
                  }}
                  label={
                    <Typography
                      variant={selectedTab === 'dataSourceItems' ? 'subtitle2' : 'caption'}
                      sx={{
                        fontWeight: selectedTab === 'dataSourceItems' ? '800' : '500',
                        color: selectedTab === 'dataSourceItems' ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      Data Source Items
                    </Typography>
                  }
                />
              )}
              {hasPermission(`pipeline:dataSources:items:view`, dataSourceInstance?.userAccess) && (
                <Tab
                  value={'runHistory'}
                  icon={<HistoryIcon fontSize="small" />}
                  iconPosition="start"
                  sx={{
                    minHeight: '40px',
                    px: 2,
                    transition: 'all 0.2s',
                    opacity: selectedTab === 'runHistory' ? 1 : 0.7,
                    '&:hover': { opacity: 1, bgcolor: 'rgba(139, 216, 188, 0.05)' },
                  }}
                  label={
                    <Typography
                      variant={selectedTab === 'runHistory' ? 'subtitle2' : 'caption'}
                      sx={{
                        fontWeight: selectedTab === 'runHistory' ? '800' : '500',
                        color: selectedTab === 'runHistory' ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      Run History
                    </Typography>
                  }
                />
              )}
              {hasPermission(`pipeline:dataSources:configuration:view`, dataSourceInstance?.userAccess) && (
                <Tab
                  value={'configuration'}
                  icon={<SettingsOutlinedIcon fontSize="small" />}
                  iconPosition="start"
                  sx={{
                    minHeight: '40px',
                    px: 3,
                    transition: 'all 0.2s',
                    opacity: selectedTab === 'configuration' ? 1 : 0.7,
                    '&:hover': { opacity: 1, bgcolor: 'rgba(139, 216, 188, 0.05)' },
                  }}
                  label={
                    <Typography
                      variant={selectedTab === 'configuration' ? 'subtitle2' : 'caption'}
                      sx={{
                        fontWeight: selectedTab === 'configuration' ? '800' : '500',
                        color: selectedTab === 'configuration' ? 'primary.main' : 'text.secondary',
                      }}
                    >
                      Configuration
                    </Typography>
                  }
                />
              )}
              {optionTabs.map((option) => (
                <Tab
                  key={option.value}
                  value={option.value}
                  icon={option.icon}
                  iconPosition="start"
                  sx={{
                    minHeight: '40px',
                    px: 3,
                    position: 'relative',
                    overflow: 'visible',
                    transition: 'all 0.2s',
                    opacity: selectedTab === option.value ? 1 : 0.7,
                    '&:hover': { opacity: 1, bgcolor: 'rgba(139, 216, 188, 0.05)' },
                  }}
                  label={
                    <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', pr: 1.5 }}>
                      <Typography
                        title={option.label}
                        variant={selectedTab === option.value ? 'subtitle2' : 'caption'}
                        sx={{ fontWeight: selectedTab === option.value ? 'bold' : 'normal', maxWidth: '160px' }}
                        noWrap
                      >
                        {option.label}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          deleteOptionTab(option.value);
                        }}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -4,
                          p: 0.1,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 10 }} />
                      </IconButton>
                    </Box>
                  }
                />
              ))}
              {openDataSourceItems.map((item) => (
                <Tab
                  key={item.id}
                  value={item.itemName}
                  icon={<SourceIcon fontSize="small" />}
                  iconPosition="start"
                  sx={{
                    minHeight: '40px',
                    px: 3,
                    position: 'relative',
                    overflow: 'visible',
                    transition: 'all 0.2s',
                    opacity: selectedTab === item.itemName ? 1 : 0.7,
                    '&:hover': { opacity: 1, bgcolor: 'rgba(139, 216, 188, 0.05)' },
                  }}
                  label={
                    <Box component="span" sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', pr: 1.5 }}>
                      <Typography
                        title={item.itemName}
                        variant={selectedTab === item.itemName ? 'subtitle2' : 'caption'}
                        sx={{ fontWeight: selectedTab === item.itemName ? 'bold' : 'normal', maxWidth: '160px' }}
                        noWrap
                      >
                        {item.itemName}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          deleteDataSourceItemTab(item.itemName);
                        }}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -4,
                          p: 0.1,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 10 }} />
                      </IconButton>
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Box>
          <Stack
            direction={'row'}
            alignItems="center"
            justifyContent="flex-end"
            spacing={1.5}
            sx={{ px: 2, py: { xs: 1, sm: 0 }, pb: { xs: 1.5, sm: 0 } }}
          >
            <Button
              size="small"
              color="primary"
              variant="contained"
              onClick={openOptionsMenu}
              endIcon={<KeyboardArrowDownIcon />}
              sx={{
                borderRadius: '20px',
                px: 2,
                bgcolor: 'primary.main',
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(35, 53, 101, 0.2)',
                '&:hover': { bgcolor: 'primary.dark', boxShadow: '0 6px 16px rgba(35, 53, 101, 0.3)' },
                textTransform: 'none',
              }}
            >
              Options
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <Box
        sx={{
          flexGrow: 1,
          overflow: 'hidden',
          mx: 1,
          mb: 0.5,
          borderRadius: '12px',
          bgcolor: 'background.paper',
          boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
          position: 'relative',
        }}
      >
        {containerHeight > 0 && topPanelHeight > 0 && (
          <>
            {hasPermission(`pipeline:dataSources:items:view`, dataSourceInstance?.userAccess) && (
              <TabPanel
                value="dataSourceItems"
                currentTab={selectedTab}
                maxHeight={containerSize.height - topPanelSize.height - 4}
              >
                <DataSourceItems
                  dataSourceInstance={dataSourceInstance}
                  onOpenDataSourceItem={onOpenDataSourceItem}
                  maxHeight={containerSize.height - topPanelSize.height - 4}
                />
              </TabPanel>
            )}

            {hasPermission(`pipeline:dataSources:items:view`, dataSourceInstance?.userAccess) && (
              <TabPanel
                value="runHistory"
                currentTab={selectedTab}
                maxHeight={containerSize.height - topPanelSize.height - 4}
              >
                <RunHistory
                  dataSourceInstance={dataSourceInstance}
                  maxHeight={containerSize.height - topPanelSize.height - 4}
                />
              </TabPanel>
            )}

            {hasPermission(`pipeline:dataSources:configuration:view`, dataSourceInstance?.userAccess) && (
              <TabPanel
                value="configuration"
                currentTab={selectedTab}
                maxHeight={containerSize.height - topPanelSize.height - 4}
              >
                <EntityConfiguration
                  maxHeight={containerSize.height - topPanelSize.height - 4}
                  entity={dataSourceInstance}
                  onSubmit={updateDataSourceInstance}
                />
              </TabPanel>
            )}

            {hasPermission(`pipeline:dataSources:update`, dataSourceInstance?.userAccess) && (
              <TabPanel
                value="pipelineBuilder"
                currentTab={selectedTab}
                maxHeight={containerSize.height - topPanelSize.height - 4}
              >
                <PipelineBuilder
                  maxHeight={containerSize.height - topPanelSize.height - 4}
                  entity={dataSourceInstance}
                  isOpen={optionTabs.find((o) => o.value === 'pipelineBuilder')}
                  onConfigurationChange={reloadDataSourceInstance}
                />
              </TabPanel>
            )}

            {(hasPermission(`pipeline:dataSources:accessControl:view`, dataSourceInstance?.userAccess) ||
              hasPermission(`pipeline:dataSources:usageStats:view`, dataSourceInstance?.userAccess) ||
              hasPermission(`pipeline:dataSources:auditTrail:view`, dataSourceInstance?.userAccess)) && (
              <TabPanel
                value="administration"
                currentTab={selectedTab}
                maxHeight={containerSize.height - topPanelSize.height - 4}
              >
                <DataSourceInstanceAdministration
                  isOpen={optionTabs.find((o) => o.value === 'administration')}
                  maxHeight={containerSize.height - topPanelSize.height - 4}
                  dataSourceInstance={dataSourceInstance}
                />
              </TabPanel>
            )}
            {openDataSourceItems.map((item) => (
              <TabPanel
                key={item.id}
                value={item.itemName}
                currentTab={selectedTab}
                maxHeight={containerSize.height - topPanelSize.height - 4}
              >
                <ViewDataSourceItem dataSourceItem={item} dataSourceInstance={dataSourceInstance} maxHeight={containerSize.height - topPanelSize.height - 4} />
              </TabPanel>
            ))}
          </>
        )}
      </Box>

      <Menu
        id="optionsMenu"
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={closeMenu}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            minWidth: 180,
          },
        }}
      >
        {hasPermission(`pipeline:dataSources:update`, dataSourceInstance?.userAccess) && (
          <MenuItem
            onClick={() =>
              addOptionsTab({
                value: 'pipelineBuilder',
                label: 'Pipeline',
                icon: <PlumbingIcon fontSize="small" />,
              })
            }
          >
            <ListItemIcon>
              <PlumbingIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>Pipeline</ListItemText>
          </MenuItem>
        )}

        {(hasPermission(`pipeline:dataSources:accessControl:view`, dataSourceInstance?.userAccess) ||
          hasPermission(`pipeline:dataSources:usageStats:view`, dataSourceInstance?.userAccess) ||
          hasPermission(`pipeline:dataSources:auditTrail:view`, dataSourceInstance?.userAccess)) && (
          <MenuItem
            onClick={() =>
              addOptionsTab({
                value: 'administration',
                label: 'Administration',
                icon: <AdminPanelSettingsIcon fontSize="small" />,
              })
            }
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}>Administration</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Stack>
  );
};

export default DataSourceInstance;
